import React, { PureComponent, createRef } from 'react';
import { connect } from 'unistore/react';
import styled, { css } from 'styled-components';
import { withSize } from 'react-sizeme';

import { scaleLinear } from 'd3';

import actions from '~/state/Actions';
import { media } from '~/styles/Utils';
import theme from '~/styles/Theme';
import { overallAboveSelector, compareAboveSelector } from '~/state/Selectors';

import AreaChart from './AreaChart';
import YAxis from './YAxis';
import IncomeLine from './IncomeLine';
import GridLines from './GridLines';
import ChartLabel from './ChartLabel';
import Tooltip from '../Tooltip';
import Resizer from './Resizer';

const RelSwitch = styled.div`
  font-size: 14px;
  text-align: center;
`;

const RelLabel = styled.label`
  display: flex;
  user-select: none;
  align-items: center;
  margin-left: 0.2em;
  cursor: pointer;

  input {
    margin-left: 0.2em;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const BtnGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Btn = styled.button`
  padding: 5px;
  flex-shrink: 0;
  cursor: pointer;
  font-size: 14px;
  display: block;
  padding: 8px 15px;
  border: none;
  background: rgb(238, 238, 238);
  outline: none;
  color: #999;

  ${(props) =>
    props.isActive
      ? css`
          background: ${(props) => props.theme.colors.blue};
          color: white;
          border-color: ${(props) => props.theme.colors.blue};
          font-weight: bold;
        `
      : ''}

  &:hover {
    background: ${(props) => props.theme.colors.blue};
    color: white;
  }
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
`;

const Svg = styled.svg`
  display: block;
  overflow: visible;
`;

const AxisLabel = styled(ChartLabel)`
  text-anchor: ${(p) => p.textAnchor || 'middle'};
`;

const ChartBgLabel = styled(ChartLabel)`
  font-size: 14px;
`;

const BigNumber = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-shadow: 0px 0px 3px rgba(255, 255, 255, 1);
  text-align: center;
  line-height: 1;

  ${media.m`
    text-align: center;
    font-size: 20px;
  `}
`;

const BgLabelContainer = styled.div`
  position: absolute;
  top: ${(p) => (p.top ? '40px' : 'auto')};
  right: ${(p) => (p.right ? '0' : 'auto')};
  left: ${(p) => (p.left ? '0' : 'auto')};
  bottom: ${(p) => (p.bottom ? '50px' : 'auto')};
  text-align: center;
  padding: 3px 4px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid ${(p) => p.color || '#777'};
  border-radius: 4px;

  ${BigNumber} {
    color: ${(p) => p.color || '#777'};
  }

  ${media.l`
    right: ${(p) => (p.right ? '15px' : 'auto')};
    left: ${(p) => (p.left ? '15px' : 'auto')};
    bottom: ${(p) => (p.bottom ? '50px' : 'auto')};
  `}
`;

const BgLabel = styled.div`
  font-size: 14px;
  text-shadow: 0px 0px 3px rgba(255, 255, 255, 1);
`;

class Visualisation extends PureComponent {
  containerRef = createRef();

  static defaultProps = {
    margin: {
      top: 10,
      right: 0,
      bottom: 40,
      left: 0,
      middle: 60,
    },
  };

  handleMouseMove(evt) {
    if (!this.containerRef.current) {
      return false;
    }

    const { top, left } = this.containerRef.current.getBoundingClientRect();
    const x = evt.clientX - left;
    const y = evt.clientY - top;

    this.props.setTooltipPosition(x, y);
  }

  render() {
    const {
      data,
      size,
      overallKey,
      compareKey,
      margin,
      overallAbove,
      compareAbove,
      isRel,
      loadData,
    } = this.props;

    if (!data) {
      return null;
    }

    const aspectRatio = window.innerWidth < 768 ? 1 : 0.7;
    const { width } = size;
    const height = Math.min(width * aspectRatio, 400);

    const chartWidth = (width - margin.middle) / 2;
    const xScaleOverall = scaleLinear()
      .domain(isRel ? [25, 0] : [12.5, 0])
      .range([0, chartWidth])
      .clamp(true)
      .nice();
    const xScaleCompare = scaleLinear()
      .domain(isRel ? [0, 25] : [0, 12.5])
      .range([0, chartWidth])
      .clamp(true)
      .nice();
    const yScale = scaleLinear()
      .domain([0, isRel ? 150 : 300])
      .range([height, 0])
      .clamp(true);
    const svgHeight = height + margin.top + margin.bottom;

    return (
      <div>
        <Resizer width={width} />
        <RelSwitch>Wohnfläche in Quadratmetern</RelSwitch>
        <BtnGroup>
          <Btn onClick={() => loadData(true)} isActive={isRel}>
            pro Kopf
          </Btn>
          <Btn onClick={() => loadData(false)} isActive={!isRel}>
            insgesamt
          </Btn>
        </BtnGroup>

        <Wrapper ref={this.containerRef} onMouseMove={(evt) => this.handleMouseMove(evt)}>
          <Tooltip />
          <BgLabelContainer top left color={theme.colors.yellow}>
            <BigNumber>{overallAbove}</BigNumber>
            <BgLabel>
              Prozent haben
              <br /> mehr Fläche
            </BgLabel>
          </BgLabelContainer>

          <BgLabelContainer bottom left color="#d5b176">
            <BigNumber>{100 - overallAbove}</BigNumber>
            <BgLabel>
              Prozent haben
              <br /> weniger Fläche
            </BgLabel>
          </BgLabelContainer>

          <BgLabelContainer bottom right color={theme.colors.lightgreen}>
            <BigNumber>{100 - compareAbove}</BigNumber>
            <BgLabel>
              Prozent haben
              <br /> weniger Fläche
            </BgLabel>
          </BgLabelContainer>

          <BgLabelContainer top right color={theme.colors.green}>
            <BigNumber>{compareAbove}</BigNumber>
            <BgLabel>
              Prozent haben
              <br /> mehr Fläche
            </BgLabel>
          </BgLabelContainer>

          <Svg width={width} height={svgHeight}>
            <g transform={`translate(${margin.left}, ${margin.top})`}>
              <GridLines width={width} yScale={yScale} />
              <AreaChart
                xScale={xScaleOverall}
                yScale={yScale}
                width={chartWidth}
                height={height}
                data={data}
                dataKey={overallKey}
                id="area_left"
                aboveColor="#CA9C4D"
                aboveHoverColor="#b18335"
                belowColor="#E0C599"
                belowHoverColor="#d5b176"
              />
              <g transform={`translate(${chartWidth + margin.middle}, 0)`}>
                <AreaChart
                  xScale={xScaleCompare}
                  yScale={yScale}
                  width={chartWidth}
                  height={height}
                  data={data}
                  dataKey={compareKey}
                  id="area_right"
                  aboveColor="#377e89"
                  aboveHoverColor="#32727c"
                  belowColor="#4197a4"
                  belowHoverColor="#3c8a96"
                />
              </g>
              <g transform={`translate(${chartWidth + margin.middle / 2}, 0)`}>
                <YAxis yScale={yScale} />
              </g>
              <IncomeLine yScale={yScale} width={width} />

              {width < 400 ? (
                <AxisLabel x={width / 2} y={height + 20} textAnchor="middle">
                  Häufigkeit der Wohnfläche
                </AxisLabel>
              ) : (
                <>
                  <AxisLabel x={width / 4} y={height + 20} textAnchor="middle">
                    Häufigkeit der Wohnfläche
                  </AxisLabel>
                  <AxisLabel x={(width / 4) * 3} y={height + 20} textAnchor="middle">
                    Häufigkeit der Wohnfläche
                  </AxisLabel>
                </>
              )}
            </g>
          </Svg>
        </Wrapper>
      </div>
    );
  }
}

export default withSize({ monitorHeight: false })(
  connect(
    (state) => ({
      data: state.distributionData,
      overallKey: state.overallKey,
      compareKey: state.compareKey,
      overallAbove: overallAboveSelector(state),
      compareAbove: compareAboveSelector(state),
      isRel: state.isRel,
    }),
    actions
  )(Visualisation)
);
